import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Truck, Smartphone, ArrowRight, Sparkles, CheckCircle2, TrendingUp } from 'lucide-react';
interface PortalCard {
  id: string;
  label: string;
  title: string;
  description: string;
  icon: ComponentType<any>;
  accent: string;
  iconBg: string;
  destination: string;
  badge?: string;
  perks: string[];
  cta: string;
}
const PORTALS: PortalCard[] = [{
  id: 'customer',
  label: 'For movers',
  title: 'Customer Portal',
  description: 'Book moves, recovery, deliveries and more. Get instant quotes or open requests to bidding.',
  icon: Users,
  accent: 'from-brand-50 to-white border-brand-200 hover:border-brand-300',
  iconBg: 'orange-gradient-bg',
  destination: '/marketplace',
  badge: 'Most popular',
  perks: ['AI-driven booking in seconds', 'Live bidding with verified carriers', 'Real-time tracking & receipts'],
  cta: 'Continue as customer'
}, {
  id: 'supplier',
  label: 'For carriers',
  title: 'Supplier Portal',
  description: 'Manage fleet, accept jobs, submit bids and track earnings — all in one dashboard.',
  icon: Truck,
  accent: 'from-blue-50 to-white border-blue-200 hover:border-blue-300',
  iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
  destination: '/supplier/login',
  perks: ['See active jobs and incoming bids', 'Counter offers and negotiate live', 'Earnings, ratings, performance'],
  cta: 'Open supplier dashboard'
}, {
  id: 'mobile',
  label: 'For customers',
  title: 'Customer App',
  description: 'The premium mobile experience for customers: AI booking, live tracking, and payments.',
  icon: Smartphone,
  accent: 'from-purple-50 to-white border-purple-200 hover:border-purple-300',
  iconBg: 'bg-gradient-to-br from-purple-500 to-fuchsia-600',
  destination: '/mobile',
  perks: ['AI booking via chat', 'Live tracking & updates', 'Seamless payments & ratings'],
  cta: 'Open customer app'
}, {
  id: 'driver',
  label: 'For drivers',
  title: 'Driver App',
  description: 'Receive jobs, navigate, and communicate with customers from one dedicated app.',
  icon: Truck,
  accent: 'from-emerald-50 to-white border-emerald-200 hover:border-emerald-300',
  iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
  destination: '/driver',
  perks: ['Accept and start jobs in one tap', 'Turn-by-turn navigation', 'Chat customers via AI or WhatsApp'],
  cta: 'Open driver app'
}];
export function PortalSelector() {
  const navigate = useNavigate();
  return <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-surface-100 to-white py-12 sm:py-16" data-id="element-2368">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2369">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12 sm:mb-16" data-id="element-2370">
          <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full bg-white text-brand-700 border border-brand-100 shadow-sm" data-id="element-2371">
            <Sparkles className="w-3.5 h-3.5" data-id="element-2372" />
            <span className="text-xs font-bold uppercase tracking-wider" data-id="element-2373">
              Welcome to Pair2
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-black text-gray-900 mb-4 tracking-tight" data-id="element-2374">
            Choose your portal
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto leading-relaxed" data-id="element-2375">
            One platform, three experiences. Pick the one that matches what
            you're here to do.
          </p>
        </motion.div>

        {/* Portal cards */}
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-id="element-2376">
          {PORTALS.map((p, i) => {
          const Icon = p.icon;
          return <motion.button key={p.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.08
          }} whileHover={{
            y: -4
          }} onClick={() => navigate(p.destination)} className={`group text-left bg-gradient-to-br ${p.accent} border-2 rounded-3xl p-5 sm:p-6 transition-all hover:shadow-xl relative overflow-hidden flex flex-col`} data-id="element-2377">
                {p.badge && <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gray-900 text-white" data-id="element-2378">
                    <TrendingUp className="w-3 h-3" data-id="element-2379" />
                    {p.badge}
                  </span>}
                {/* glow accent */}
                <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full bg-white/40 blur-3xl pointer-events-none" data-id="element-2380" />

                <div className="relative flex-1 flex flex-col" data-id="element-2381">
                  <div className={`w-12 h-12 rounded-2xl ${p.iconBg} flex items-center justify-center shadow-lg mb-4`} data-id="element-2382">
                    <Icon className="w-6 h-6 text-white" data-id="element-2383" />
                  </div>

                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1" data-id="element-2384">
                    {p.label}
                  </p>
                  <h3 className="text-xl font-display font-black text-gray-900 mb-2" data-id="element-2385">
                    {p.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed mb-4 flex-1" data-id="element-2386">
                    {p.description}
                  </p>

                  <ul className="space-y-1.5 mb-5" data-id="element-2387">
                    {p.perks.map(perk => <li key={perk} className="text-[11px] text-gray-700 flex items-start gap-2" data-id="element-2388">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" data-id="element-2389" />
                        <span data-id="element-2390">{perk}</span>
                      </li>)}
                  </ul>

                  <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-auto" data-id="element-2391">
                    <span className="text-xs font-bold text-gray-900" data-id="element-2392">
                      {p.cta}
                    </span>
                    <span className="w-9 h-9 rounded-full bg-gray-900 group-hover:bg-black text-white flex items-center justify-center transition-colors group-hover:translate-x-1 transition-transform" data-id="element-2393">
                      <ArrowRight className="w-4 h-4" data-id="element-2394" />
                    </span>
                  </div>
                </div>
              </motion.button>;
        })}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-12" data-id="element-2395">
          You can switch between portals anytime from the top-right menu.
        </p>
      </div>
    </div>;
}